import React, { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import TopBar from './TopBar';
import UserManagementSidebar from './UserManagementSidebar';
import ControllerWidget from './ControllerWidget';
import UserManagementContent from './UserManagementContent';
import AddUserRoleDialog from './AddUserRoleDialog';
import { Permissions } from '../services/permissionService';

interface UserManagementPageProps {
  onBack: () => void;
  onAuthClick: () => void;
}

type UserManagementView = 'roles' | 'users' | 'userroles' | 'subscriptions';

const UserManagementPage: React.FC<UserManagementPageProps> = ({ onBack, onAuthClick }) => {
  const [selectedView, setSelectedView] = useState<UserManagementView>('roles');
  const [searchFilter, setSearchFilter] = useState<{column: string, value: string} | null>(null);
  const [tableColumns, setTableColumns] = useState<string[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [allData, setAllData] = useState<any[]>([]);
  const [roleAddTrigger, setRoleAddTrigger] = useState(0);
  const [userRoleAddTrigger, setUserRoleAddTrigger] = useState(0);
  const [userAddTrigger, setUserAddTrigger] = useState(0);

  // Jogosultságok mapping a selectedView alapján
  const getPermissionsForView = () => {
    switch (selectedView) {
      case 'roles':
        return {
          create: Permissions.CreateRoles,
          edit: Permissions.EditRoles,
          delete: Permissions.DeleteRoles,
        };
      case 'users':
        return {
          create: Permissions.CreateUsers,
          edit: Permissions.EditUsers,
          delete: Permissions.DeleteUsers,
        };
      case 'userroles':
        return {
          create: Permissions.ManageUserRoles,
          edit: undefined,
          delete: Permissions.ManageUserRoles,
        };
      case 'subscriptions':
        return {
          create: Permissions.ManageBilling,
          edit: Permissions.ManageBilling,
          delete: Permissions.ManageBilling,
        };
      default:
        return { create: undefined, edit: undefined, delete: undefined };
    }
  };

  const permissions = getPermissionsForView();

  const handleSearch = (column: string, searchValue: string) => {
    if (searchValue.trim()) {
      setSearchFilter({ column, value: searchValue });
    } else {
      setSearchFilter(null);
    }
  };

  const handleColumnsUpdate = (columns: string[]) => {
    setTableColumns(columns);
  };

  const handleDataUpdate = (data: any[]) => {
    setAllData(data);
  };

  const handleGetSuggestions = useCallback(async (searchValue: string): Promise<string[]> => {
    
    if (!allData || allData.length === 0 || searchValue.length < 3) {
      return [];
    }

    const lowerSearchValue = searchValue.toLowerCase();
    const uniqueValues = new Set<string>();

    // Collect all unique values from all rows that match the search
    allData.forEach(item => {
      Object.values(item).forEach(value => {
        if (value != null) {
          const stringValue = value.toString();
          if (stringValue.toLowerCase().includes(lowerSearchValue)) {
            uniqueValues.add(stringValue);
          }
        }
      });
    });

    // Return top 10 suggestions sorted by relevance
    return Array.from(uniqueValues)
      .sort((a, b) => {
        // Prioritize matches that start with the search value
        const aStarts = a.toLowerCase().startsWith(lowerSearchValue);
        const bStarts = b.toLowerCase().startsWith(lowerSearchValue);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.localeCompare(b);
      })
      .slice(0, 10);
  }, [allData]);

  const handleVisibleColumnsChange = (columns: string[]) => {
    setVisibleColumns(columns);
  };

  const handleAddRecord = () => {
    if (selectedView === 'roles') {
      // Trigger the RolesTable to open its add dialog
      setRoleAddTrigger(prev => prev + 1);
    } else if (selectedView === 'userroles') {
      // Open the user role dialog directly
      setAddDialogOpen(true);
    } else if (selectedView === 'users') {
      // Trigger the UsersTable (or could open add user dialog if exists)
      setUserAddTrigger(prev => prev + 1);
    } else {
      console.log('Add new record for:', selectedView);
    }
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
  };

  const handleAddSuccess = () => {
    // Trigger a refresh of the main content
    setRefreshTrigger(prev => prev + 1);
  };

  const handleUserRoleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleUserAddClick = () => {
    // Could open AddUserDialog here if it exists
    console.log('Add user clicked');
  };

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Left Panel - Sidebar */}
      <Box
        sx={{
          width: 240,
          minWidth: 240,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <UserManagementSidebar 
          selectedView={selectedView} 
          setSelectedView={setSelectedView} 
        />
      </Box>
      
      {/* Right Panel - Same structure as RightPanel */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          height: '100vh',
          minWidth: 0, // Prevents flex item from growing beyond container
          overflow: 'hidden', // Prevents content from overflowing
        }}
      >
        <TopBar onAuthClick={onAuthClick} />
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#f0f3f6',
          p: 2,
          gap: 2, // This creates equal spacing between ControllerWidget and MainContent
        }}>
          <ControllerWidget
            selectedMenu={selectedView}
            tableColumns={tableColumns}
            onSearch={handleSearch}
            onVisibleColumnsChange={handleVisibleColumnsChange}
            onAddRecord={handleAddRecord}
            onGetSuggestions={handleGetSuggestions}
            createPermission={permissions.create}
          />
          <UserManagementContent
            selectedView={selectedView}
            searchFilter={searchFilter}
            onColumnsUpdate={handleColumnsUpdate}
            visibleColumns={visibleColumns}
            refreshTrigger={refreshTrigger}
            onDataUpdate={handleDataUpdate}
            roleAddTrigger={roleAddTrigger}
            userRoleAddTrigger={userRoleAddTrigger}
            onUserRoleAddClick={handleUserRoleAddClick}
            userRoleDeletePermission={permissions.delete}
            userAddTrigger={userAddTrigger}
            onUserAddClick={handleUserAddClick}
            userEditPermission={permissions.edit}
            userDeletePermission={permissions.delete}
          />
        </Box>
      </Box>

      {/* Add UserRole Dialog */}
      {selectedView === 'userroles' && (
        <AddUserRoleDialog
          open={addDialogOpen}
          onClose={handleAddDialogClose}
          onSuccess={handleAddSuccess}
        />
      )}
    </Box>
  );
};

export default UserManagementPage;
